import { useEffect, useState } from "react";
import { Check, EyeOff, Loader2, Sparkles, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Stars } from "@/components/ProductReviews";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const ReviewCard = ({ r, onModerate, onDelete, onGenerateReply, onSaveReply, replyDraft, generating, savingReply, onDraftChange }) => (
  <div data-testid={`admin-review-${r.id}`} className="border border-clay bg-white p-5">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-bark" data-testid={`admin-review-author-${r.id}`}>{r.author_name}</span>
          <Stars value={r.rating} />
          <span
            data-testid={`admin-review-status-${r.id}`}
            className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
              r.approved ? "bg-terracotta/10 text-terracotta" : "bg-ochre/20 text-bark"
            }`}
          >
            {r.approved ? "Publié" : "En attente"}
          </span>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-bark/70">{r.comment}</p>
        <p className="mt-2 text-xs text-bark/45">
          {r.product_name || "Produit"} — {formatDate(r.created_at)}
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        {r.approved ? (
          <button
            data-testid={`admin-review-unpublish-${r.id}`}
            onClick={() => onModerate(r, false)}
            className="flex h-9 items-center gap-2 rounded-full border border-clay px-4 text-[10px] font-bold uppercase tracking-widest text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
          >
            <EyeOff className="h-3.5 w-3.5" /> Retirer
          </button>
        ) : (
          <button
            data-testid={`admin-review-approve-${r.id}`}
            onClick={() => onModerate(r, true)}
            className="flex h-9 items-center gap-2 rounded-full bg-terracotta px-4 text-[10px] font-bold uppercase tracking-widest text-sand transition-colors hover:bg-terracotta-dark"
          >
            <Check className="h-3.5 w-3.5" /> Approuver
          </button>
        )}
        <button
          data-testid={`admin-review-delete-${r.id}`}
          onClick={() => onDelete(r)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-clay text-bark/40 transition-colors hover:border-destructive hover:text-destructive"
          aria-label="Supprimer cet avis"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>

    <div className="mt-4 border-t border-clay/60 pt-4">
      {r.admin_reply && replyDraft === null && (
        <div className="border-l-2 border-terracotta bg-cream p-4" data-testid={`admin-review-reply-display-${r.id}`}>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-terracotta">Votre réponse (visible sur le site)</p>
          <p className="mt-1 text-sm leading-relaxed text-bark/70">{r.admin_reply}</p>
        </div>
      )}
      {replyDraft !== null ? (
        <div className="mt-3">
          <textarea
            data-testid={`admin-review-reply-input-${r.id}`}
            value={replyDraft}
            onChange={(e) => onDraftChange(e.target.value)}
            rows={3}
            maxLength={1000}
            className="w-full rounded-md border border-terracotta bg-cream px-3 py-3 text-sm text-bark focus:outline-none"
          />
          <div className="mt-2 flex gap-2">
            <button
              data-testid={`admin-review-reply-save-${r.id}`}
              onClick={() => onSaveReply(r)}
              disabled={savingReply}
              className="flex h-9 items-center gap-2 rounded-full bg-terracotta px-4 text-[10px] font-bold uppercase tracking-widest text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
            >
              {savingReply && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Publier la réponse
            </button>
            <button
              data-testid={`admin-review-reply-cancel-${r.id}`}
              onClick={() => onDraftChange(null)}
              className="flex h-9 items-center rounded-full border border-clay px-4 text-[10px] font-bold uppercase tracking-widest text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <button
          data-testid={`admin-review-reply-ai-${r.id}`}
          onClick={() => onGenerateReply(r)}
          disabled={generating}
          className="mt-3 flex h-9 items-center gap-2 rounded-full border border-terracotta px-4 text-[10px] font-bold uppercase tracking-widest text-terracotta transition-colors hover:bg-terracotta hover:text-sand disabled:opacity-50"
        >
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {generating ? "Rédaction en cours…" : r.admin_reply ? "Régénérer une réponse avec l'IA" : "Répondre avec l'IA"}
        </button>
      )}
    </div>
  </div>
);

const AdminReviews = () => {
  const [reviews, setReviews] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyDraft, setReplyDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [savingReply, setSavingReply] = useState(false);

  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));

  useEffect(() => {
    load();
  }, []);

  const moderate = async (review, approved) => {
    try {
      await api.patch(`/admin/reviews/${review.id}`, { approved });
      setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, approved } : r)));
      toast.success(approved ? `Avis de ${review.author_name} publié` : `Avis de ${review.author_name} retiré`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const generateReply = async (review) => {
    setReplyFor(review.id);
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/ai/review-reply", { review_id: review.id });
      setReplyDraft(data.reply);
    } catch (err) {
      setReplyFor(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const saveReply = async (review) => {
    setSavingReply(true);
    try {
      await api.patch(`/admin/reviews/${review.id}`, { approved: review.approved, admin_reply: replyDraft.trim() });
      setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, admin_reply: replyDraft.trim() } : r)));
      setReplyFor(null);
      setReplyDraft(null);
      toast.success("Réponse publiée sous l'avis");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingReply(false);
    }
  };

  const remove = async (review) => {
    try {
      await api.delete(`/admin/reviews/${review.id}`);
      setReviews((prev) => prev.filter((r) => r.id !== review.id));
      setConfirmDelete(null);
      toast.success("Avis supprimé");
    } catch (err) {
      setConfirmDelete(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (reviews === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
      </div>
    );
  }

  const pending = reviews.filter((r) => !r.approved);
  const published = reviews.filter((r) => r.approved);
  const cardProps = (r) => ({
    r,
    onModerate: moderate,
    onDelete: setConfirmDelete,
    onGenerateReply: generateReply,
    onSaveReply: saveReply,
    replyDraft: replyFor === r.id ? replyDraft : null,
    generating: generating && replyFor === r.id,
    savingReply: savingReply && replyFor === r.id,
    onDraftChange: (v) => (v === null ? (setReplyFor(null), setReplyDraft(null)) : setReplyDraft(v)),
  });

  return (
    <section data-testid="admin-reviews">
      <h1 className="font-display text-3xl font-semibold text-bark">Avis clients</h1>
      <p className="mt-1 text-sm text-bark/50">
        Les avis déposés par les clients n'apparaissent sur le site qu'après votre approbation. Vous pouvez aussi y répondre avec l'aide de l'IA.
      </p>

      <div className="mt-8">
        <h2 className="font-display text-xl font-semibold text-bark">
          En attente de validation <span className="ml-2 rounded-full bg-ochre/20 px-2.5 py-0.5 text-xs font-bold text-bark">{pending.length}</span>
        </h2>
        <div className="mt-4 space-y-3" data-testid="admin-reviews-pending">
          {pending.length === 0 ? (
            <p className="border border-clay bg-cream p-6 text-sm text-bark/40">Aucun avis en attente.</p>
          ) : (
            pending.map((r) => <ReviewCard key={r.id} {...cardProps(r)} />)
          )}
        </div>
      </div>

      <div className="mt-10">
        <h2 className="font-display text-xl font-semibold text-bark">
          Publiés <span className="ml-2 rounded-full bg-terracotta/10 px-2.5 py-0.5 text-xs font-bold text-terracotta">{published.length}</span>
        </h2>
        <div className="mt-4 space-y-3" data-testid="admin-reviews-published">
          {published.length === 0 ? (
            <p className="border border-clay bg-cream p-6 text-sm text-bark/40">Aucun avis publié pour le moment.</p>
          ) : (
            published.map((r) => <ReviewCard key={r.id} {...cardProps(r)} />)
          )}
        </div>
      </div>

      {confirmDelete && (
        <div
          data-testid="delete-review-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bark/50 p-6 backdrop-blur-sm"
          onClick={() => setConfirmDelete(null)}
        >
          <div className="w-full max-w-md border border-clay bg-cream p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-semibold text-bark">Supprimer cet avis ?</h3>
            <p className="mt-3 text-sm leading-relaxed text-bark/60">
              L'avis de {confirmDelete.author_name} sera supprimé définitivement. Cette action est irréversible.
            </p>
            <div className="mt-8 flex gap-3">
              <button
                data-testid="delete-review-confirm-yes"
                onClick={() => remove(confirmDelete)}
                className="flex h-11 flex-1 items-center justify-center rounded-full bg-destructive text-xs font-bold uppercase tracking-[0.15em] text-sand transition-opacity hover:opacity-90"
              >
                Supprimer
              </button>
              <button
                data-testid="delete-review-confirm-no"
                onClick={() => setConfirmDelete(null)}
                className="flex h-11 flex-1 items-center justify-center rounded-full border border-clay text-xs font-bold uppercase tracking-[0.15em] text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminReviews;
